import { AxiosResponse } from 'axios';
import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';
import { mapFxCategory } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import { asResponse, throwIfError } from '@/lib/supabase-helpers';
import { FxCategory } from '@/types/api/finance';

const financeCategoriesService = {
  async getAll(): Promise<AxiosResponse<FxCategory[]>> {
    let query = supabase
      .from('fx_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) throw error;
    return asResponse((data ?? []).map(mapFxCategory));
  },

  async create(payload: {
    name: string;
    slug: string;
    parentId?: string;
    color?: string;
    icon?: string;
  }): Promise<AxiosResponse<FxCategory>> {
    const orgId = getActiveOrgId();
    const insert: Record<string, unknown> = {
      name: payload.name,
      slug: payload.slug,
      ...(orgId ? { organization_id: orgId } : {}),
    };
    if (payload.parentId !== undefined) insert.parent_id = payload.parentId;
    if (payload.color !== undefined) insert.color = payload.color;
    if (payload.icon !== undefined) insert.icon = payload.icon;

    const result = await supabase.from('fx_categories').insert(insert).select().single();

    return asResponse(mapFxCategory(throwIfError(result)));
  },

  async update(
    id: string,
    payload: {
      name?: string;
      slug?: string;
      parentId?: string | null;
      color?: string;
      icon?: string;
      isActive?: boolean;
    }
  ): Promise<AxiosResponse<FxCategory>> {
    const updates: Record<string, unknown> = {};
    if (payload.name !== undefined) updates.name = payload.name;
    if (payload.slug !== undefined) updates.slug = payload.slug;
    if (payload.parentId !== undefined) updates.parent_id = payload.parentId;
    if (payload.color !== undefined) updates.color = payload.color;
    if (payload.icon !== undefined) updates.icon = payload.icon;
    if (payload.isActive !== undefined) updates.is_active = payload.isActive;

    const result = await supabase
      .from('fx_categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    return asResponse(mapFxCategory(throwIfError(result)));
  },

  async delete(id: string): Promise<AxiosResponse<void>> {
    const { error } = await supabase.from('fx_categories').delete().eq('id', id);

    if (error) throw error;
    return asResponse(undefined as void);
  },
};

export default financeCategoriesService;
